import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tv, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/services/api";

export default function QrApprovePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading, user } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | approving | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate(`/login?return=/qr-approve/${code}`, { replace: true });
    }
  }, [loading, isAuthenticated, navigate, code]);

  const handleApprove = async () => {
    setStatus("approving");
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/qr/${code}/approve`, {
        auth_token: api.getToken(),
      });
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Xəta baş verdi. Yenidən cəhd edin");
      setStatus("error");
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        {status === "success" ? (
          <div data-testid="qr-approve-success" className="animate-fade-in">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-5" />
            <h1 className="text-xl font-bold text-white mb-2">TV-də giriş təsdiqləndi</h1>
            <p className="text-sm text-zinc-400 mb-8">Televizorunuz bir neçə saniyə ərzində avtomatik daxil olacaq</p>
            <button
              onClick={() => navigate("/app")}
              className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium transition-colors"
            >
              App-a qayıt
            </button>
          </div>
        ) : (
          <>
            <div className="w-20 h-20 rounded-3xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mx-auto mb-6">
              <Tv className="w-10 h-10 text-blue-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">TV-də daxil olun</h1>
            <p className="text-sm text-zinc-400 mb-2">
              <span className="text-blue-400 font-medium">{user?.username || "Hesabınız"}</span> ilə televizorda giriş təsdiqlənsin?
            </p>
            <p className="text-xs text-zinc-600 mb-8">Yalnız öz televizorunuzdakı QR kodu oxutduğunuzdan əmin olun</p>
            {error && (
              <div data-testid="qr-approve-error" className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm mb-5 text-left">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => navigate("/app")}
                className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Ləğv et
              </button>
              <button
                data-testid="qr-approve-btn"
                onClick={handleApprove}
                disabled={status === "approving"}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {status === "approving" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Təsdiqlə
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
